import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmd, resolveAppLocale } from '../locale.js';
import type { Database } from '@cafe-pos/supabase';
import {
  calcLoyaltyCheckout,
  defaultRedeemAmdPerCoin,
  generateMemberCode,
  LOYALTY_PASS_CARD_TITLE,
  LOYALTY_POINT_NAME,
  maxCategoryDiscountPercent,
  parseCategoryDiscounts,
  sanitizeCategoryDiscounts,
  slugifyJoinLinkName,
  type CategoryDiscountMap,
} from '@cafe-pos/shared';
import { LoyaltyMemberQr } from '../components/LoyaltyMemberQr.js';
import { buildLoyaltyJoinUrl } from '../loyaltyJoin.js';
import { supabase } from '../supabase.js';
import { PageHeader } from '../ui/index.js';

type SettingsRow = Database['public']['Tables']['loyalty_settings']['Row'];
type MemberRow = Database['public']['Tables']['loyalty_members']['Row'];
type LinkRow = Database['public']['Tables']['loyalty_join_links']['Row'];

const COLOR_PRESETS = ['dark', 'blue', 'green', 'red', 'purple', 'orange'] as const;
const POINT = LOYALTY_POINT_NAME.toLowerCase();

const AMD = (n: number) => `${Math.round(n).toLocaleString('en-US')} AMD`;

function emptyCategoryDiscounts(): CategoryDiscountMap {
  return {};
}

function formatCategoryDiscountSummary(
  map: CategoryDiscountMap,
  flatPercent: number,
  categories: { id: string; name: string }[],
): string {
  const entries = categories
    .map((c) => ({ name: c.name, pct: map[c.id] ?? 0 }))
    .filter((e) => e.pct > 0);
  if (entries.length === 0) {
    return flatPercent > 0 ? `${flatPercent}% all` : '—';
  }
  if (entries.length <= 2) {
    return entries.map((e) => `${e.name} ${e.pct}%`).join(', ');
  }
  return `Up to ${maxCategoryDiscountPercent(map, flatPercent)}%`;
}

function clampPercent(raw: string): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(100, Math.round(n));
}

function CategoryDiscountEditor({
  categories,
  values,
  onChange,
}: {
  categories: { id: string; name: string }[];
  values: CategoryDiscountMap;
  onChange: (next: CategoryDiscountMap) => void;
}) {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [chipDraft, setChipDraft] = useState('');

  // "All categories" input mirrors the map when every category has the same rate.
  const uniform = useMemo(() => {
    if (categories.length === 0) return 0;
    const first = values[categories[0]!.id] ?? 0;
    return categories.every((c) => (values[c.id] ?? 0) === first) ? first : null;
  }, [categories, values]);

  if (categories.length === 0) {
    return <p className="muted" style={{ fontSize: 12, margin: 0 }}>{t('loyalty.noCategories')}</p>;
  }

  function applyToAll(raw: string) {
    const pct = clampPercent(raw);
    const next: CategoryDiscountMap = {};
    if (pct > 0) for (const c of categories) next[c.id] = pct;
    onChange(next);
    setEditingId(null);
  }

  function startChipEdit(catId: string) {
    const pct = values[catId] ?? 0;
    setChipDraft(pct > 0 ? String(pct) : '');
    setEditingId(catId);
  }

  function commitChip(catId: string) {
    const pct = clampPercent(chipDraft);
    const next = { ...values };
    if (pct <= 0) delete next[catId];
    else next[catId] = pct;
    onChange(next);
    setEditingId(null);
  }

  return (
    <div className="loyalty-discount-editor">
      <label className="loyalty-discount-all">
        <span>{t('loyalty.allCategories')}</span>
        <input
          className="loyalty-table-input loyalty-table-input-narrow"
          type="number"
          min={0}
          max={100}
          step={1}
          value={uniform === null ? '' : uniform || ''}
          placeholder={uniform === null ? t('loyalty.mixed') : '0'}
          aria-label={t('loyalty.allCategories')}
          onChange={(e) => applyToAll(e.target.value)}
        />
        <span className="muted">%</span>
      </label>
      <div className="loyalty-discount-chips" role="group" aria-label={t('loyalty.discountByCategory')}>
        {categories.map((cat) => {
          const pct = values[cat.id] ?? 0;
          if (editingId === cat.id) {
            return (
              <span key={cat.id} className="loyalty-discount-chip editing">
                <span className="loyalty-discount-chip-name">{cat.name}</span>
                <input
                  autoFocus
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={chipDraft}
                  placeholder="0"
                  aria-label={t('loyalty.discountPercentAria', { name: cat.name })}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setChipDraft(e.target.value)}
                  onBlur={() => commitChip(cat.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      commitChip(cat.id);
                    } else if (e.key === 'Escape') {
                      e.stopPropagation();
                      setEditingId(null);
                    }
                  }}
                />
                <span>%</span>
              </span>
            );
          }
          return (
            <button
              key={cat.id}
              type="button"
              className={`loyalty-discount-chip${pct > 0 ? ' active' : ''}`}
              title={`Change ${cat.name} discount`}
              onClick={() => startChipEdit(cat.id)}
            >
              <span className="loyalty-discount-chip-name">{cat.name}</span>
              <span className="loyalty-discount-chip-pct">{pct}%</span>
            </button>
          );
        })}
      </div>
      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        Tap a category to set its own rate — 0% means no discount for that category.
      </p>
    </div>
  );
}

function formatMemberSource(
  member: MemberRow,
  linkById: Map<string, LinkRow>,
  clientId: string | null,
  t: (key: string) => string,
) {
  if (!member.join_link_id) {
    return <span className="muted">{t('loyalty.manual')}</span>;
  }
  const link = linkById.get(member.join_link_id);
  if (!link) {
    return <span className="muted">{t('loyalty.linkRemoved')}</span>;
  }
  const url = clientId ? buildLoyaltyJoinUrl(clientId, link.slug) : null;
  return (
    <span>
      {link.name}
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="muted"
          style={{ display: 'block', fontSize: 11, fontFamily: 'monospace' }}
          title={url}
        >
          {link.slug}
        </a>
      ) : (
        <code className="muted" style={{ display: 'block', fontSize: 11 }}>
          {link.slug}
        </code>
      )}
    </span>
  );
}

function normalizePhone(raw: string): string {
  return raw.replace(/\D/g, '');
}

function RateExample({
  earnAmdPerCoin,
  redeemAmdPerCoin,
}: {
  earnAmdPerCoin: number;
  redeemAmdPerCoin: number;
}) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const spend = earnAmdPerCoin * 100;
  const coinsEarned = Math.floor(spend / earnAmdPerCoin);

  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'var(--surface-2)',
        borderRadius: 'var(--r-md)',
        fontSize: 13,
        lineHeight: 1.55,
      }}
    >
      <p style={{ margin: '0 0 8px', fontWeight: 600 }}>{t('loyalty.exampleTitle')}</p>
      <p style={{ margin: 0 }}>
        {t('loyalty.exampleSpend', {
          amount: AMD(spend),
          coins: coinsEarned,
          pointName: POINT,
          earnRate: earnAmdPerCoin.toLocaleString(resolveAppLocale(i18n.language)),
        })}
      </p>
      <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
        {t('loyalty.redeemHint')}
      </p>
    </div>
  );
}

export function LoyaltyManager({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const [settings, setSettings] = useState<SettingsRow | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [cafeName, setCafeName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const [newLinkName, setNewLinkName] = useState('');
  const [newLinkCategoryDiscounts, setNewLinkCategoryDiscounts] = useState<CategoryDiscountMap>(
    emptyCategoryDiscounts,
  );
  const [newLinkCollectCoins, setNewLinkCollectCoins] = useState(true);
  const [newLinkAllowCustomNumbers, setNewLinkAllowCustomNumbers] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  // Inline link editing
  const [editLinkId, setEditLinkId] = useState<string | null>(null);
  const [editLinkName, setEditLinkName] = useState('');
  const [editLinkCategoryDiscounts, setEditLinkCategoryDiscounts] = useState<CategoryDiscountMap>(
    emptyCategoryDiscounts,
  );
  const [editLinkCollectCoins, setEditLinkCollectCoins] = useState(true);
  const [editLinkAllowCustomNumbers, setEditLinkAllowCustomNumbers] = useState(false);
  const [rateSummaryOpen, setRateSummaryOpen] = useState(false);

  const [memberFormOpen, setMemberFormOpen] = useState(false);
  const [memberBusy, setMemberBusy] = useState(false);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberPhone, setNewMemberPhone] = useState('');
  const [newMemberCategoryDiscounts, setNewMemberCategoryDiscounts] = useState<CategoryDiscountMap>(
    emptyCategoryDiscounts,
  );
  const [newMemberCollectCoins, setNewMemberCollectCoins] = useState(true);
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [memberSourceFilter, setMemberSourceFilter] = useState<string>('all');
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(() => new Set());
  const [qrModal, setQrModal] = useState<{ memberId: string; memberCode: string; label?: string } | null>(null);

  const earnAmdPerCoin = Number(settings?.earn_amd_per_coin) || 50;
  const redeemAmdPerCoin =
    Number(settings?.redeem_amd_per_coin) || defaultRedeemAmdPerCoin(earnAmdPerCoin);

  const posPreview = useMemo(
    () =>
      calcLoyaltyCheckout({
        subtotal: 10000,
        coinBalance: 500,
        discountPercent: 10,
        earnAmdPerCoin,
        redeemAmdPerCoin,
      }),
    [earnAmdPerCoin, redeemAmdPerCoin],
  );

  const linkById = useMemo(() => new Map(links.map((l) => [l.id, l])), [links]);

  const filteredMembers = useMemo(() => {
    if (memberSourceFilter === 'all') return members;
    if (memberSourceFilter === 'manual') {
      return members.filter((m) => !m.join_link_id);
    }
    if (memberSourceFilter === 'link_removed') {
      return members.filter((m) => m.join_link_id && !linkById.has(m.join_link_id));
    }
    return members.filter((m) => m.join_link_id === memberSourceFilter);
  }, [members, memberSourceFilter, linkById]);

  const selectedFilteredCount = useMemo(
    () => filteredMembers.filter((m) => selectedMemberIds.has(m.id)).length,
    [filteredMembers, selectedMemberIds],
  );

  const allFilteredSelected =
    filteredMembers.length > 0 && selectedFilteredCount === filteredMembers.length;
  const membersBusy = deletingMemberId !== null || bulkDeleting;

  async function load() {
    if (!supabase || !clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const [settingsRes, membersRes, linksRes, clientRes, categoriesRes] = await Promise.all([
      supabase.from('loyalty_settings').select('*').eq('client_id', clientId).maybeSingle(),
      supabase
        .from('loyalty_members')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(200),
      supabase
        .from('loyalty_join_links')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: true }),
      supabase.from('clients').select('name').eq('id', clientId).single(),
      supabase
        .from('categories')
        .select('id, name')
        .eq('client_id', clientId)
        .order('sort_order')
        .order('name'),
    ]);
    setSettings(settingsRes.data);
    setMembers(membersRes.data ?? []);
    setLinks(linksRes.data ?? []);
    setCategories(categoriesRes.data ?? []);
    setCafeName(clientRes.data?.name ?? '');
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, [clientId]);

  async function save() {
    if (!supabase || !clientId || !settings) return;
    setSaving(true);
    setMsg(null);
    const earn = Number(settings.earn_amd_per_coin) || 50;
    const redeem = Number(settings.redeem_amd_per_coin) || defaultRedeemAmdPerCoin(earn);
    const { data, error } = await supabase
      .from('loyalty_settings')
      .update({
        enabled: settings.enabled,
        earn_amd_per_coin: earn,
        redeem_amd_per_coin: Math.max(0.01, redeem),
        coin_name: LOYALTY_POINT_NAME,
        pass_logo_text: settings.pass_logo_text.trim() || cafeName,
        pass_color_preset: settings.pass_color_preset,
        updated_at: new Date().toISOString(),
      })
      .eq('client_id', clientId)
      .select('client_id')
      .maybeSingle();
    setSaving(false);
    if (error) setMsg({ text: error.message, ok: false });
    else if (!data) {
      setMsg({ text: t('loyalty.notProvisioned'), ok: false });
    } else {
      setMsg({ text: t('loyalty.saved'), ok: true });
      void load();
    }
  }

  async function createLink() {
    if (!supabase || !clientId || !newLinkName.trim()) return;
    setLinkBusy(true);
    setMsg(null);
    const slug = slugifyJoinLinkName(newLinkName);
    const categoryDiscounts = sanitizeCategoryDiscounts(newLinkCategoryDiscounts);
    const discountPercent = maxCategoryDiscountPercent(categoryDiscounts, 0);
    const { error } = await supabase.from('loyalty_join_links').insert({
      client_id: clientId,
      name: newLinkName.trim(),
      slug,
      discount_percent: discountPercent,
      category_discounts: categoryDiscounts,
      collect_coins: newLinkCollectCoins,
      allow_custom_numbers: newLinkAllowCustomNumbers,
      active: true,
    });
    setLinkBusy(false);
    if (error) {
      setMsg({ text: error.message.includes('unique') ? t('loyalty.duplicateName') : error.message, ok: false });
      return;
    }
    setNewLinkName('');
    setNewLinkCategoryDiscounts(emptyCategoryDiscounts());
    setNewLinkCollectCoins(true);
    setNewLinkAllowCustomNumbers(false);
    setLinkFormOpen(false);
    setMsg({ text: t('loyalty.created'), ok: true });
    void load();
  }

  function closeLinkForm() {
    setLinkFormOpen(false);
    setNewLinkName('');
    setNewLinkCategoryDiscounts(emptyCategoryDiscounts());
    setNewLinkCollectCoins(true);
    setNewLinkAllowCustomNumbers(false);
  }

  async function toggleLinkActive(link: LinkRow) {
    if (!supabase) return;
    await supabase.from('loyalty_join_links').update({ active: !link.active }).eq('id', link.id);
    void load();
  }

  function startEditLink(link: LinkRow) {
    setEditLinkId(link.id);
    setEditLinkName(link.name);
    setEditLinkCategoryDiscounts(parseCategoryDiscounts(link.category_discounts));
    setEditLinkCollectCoins(link.collect_coins !== false);
    setEditLinkAllowCustomNumbers(link.allow_custom_numbers === true);
  }

  function cancelEditLink() {
    setEditLinkId(null);
  }

  async function saveEditLink() {
    if (!supabase || !editLinkId || !editLinkName.trim()) return;
    setLinkBusy(true);
    setMsg(null);
    const categoryDiscounts = sanitizeCategoryDiscounts(editLinkCategoryDiscounts);
    const discountPercent = maxCategoryDiscountPercent(categoryDiscounts, 0);
    // Slug stays fixed on rename — existing QR codes and shared URLs keep working.
    const { error } = await supabase
      .from('loyalty_join_links')
      .update({
        name: editLinkName.trim(),
        discount_percent: discountPercent,
        category_discounts: categoryDiscounts,
        collect_coins: editLinkCollectCoins,
        allow_custom_numbers: editLinkAllowCustomNumbers,
      })
      .eq('id', editLinkId);
    setLinkBusy(false);
    if (error) {
      setMsg({ text: error.message, ok: false });
      return;
    }
    setEditLinkId(null);
    setMsg({ text: t('loyalty.updated'), ok: true });
    void load();
  }

  function closeMemberForm() {
    setMemberFormOpen(false);
    setNewMemberName('');
    setNewMemberPhone('');
    setNewMemberCategoryDiscounts(emptyCategoryDiscounts());
    setNewMemberCollectCoins(true);
  }

  async function createMember() {
    if (!supabase || !clientId) return;

    const displayName = newMemberName.trim();
    if (!displayName) {
      setMsg({ text: t('loyalty.enterName'), ok: false });
      return;
    }

    setMemberBusy(true);
    setMsg(null);

    const phone = newMemberPhone.trim() ? normalizePhone(newMemberPhone.trim()) : null;
    const categoryDiscounts = sanitizeCategoryDiscounts(newMemberCategoryDiscounts);
    const discountPercent = maxCategoryDiscountPercent(categoryDiscounts, 0);

    for (let attempt = 0; attempt < 8; attempt++) {
      const memberCode = generateMemberCode();
      const { data, error } = await supabase
        .from('loyalty_members')
        .insert({
          client_id: clientId,
          member_code: memberCode,
          phone,
          display_name: displayName,
          discount_percent: discountPercent,
          category_discounts: categoryDiscounts,
          collect_coins: newMemberCollectCoins,
          wallet_serial: null,
          wallet_share_url: null,
          join_link_id: null,
        })
        .select('id, member_code, display_name')
        .maybeSingle();

      if (data) {
        setMemberBusy(false);
        closeMemberForm();
        setMsg({ text: t('loyalty.memberCreated'), ok: true });
        setQrModal({
          memberId: data.id,
          memberCode: data.member_code,
          label: data.display_name ?? undefined,
        });
        void load();
        return;
      }

      if (error && !error.message.includes('unique') && !error.message.includes('duplicate')) {
        setMemberBusy(false);
        setMsg({ text: error.message, ok: false });
        return;
      }
    }

    setMemberBusy(false);
    setMsg({ text: t('loyalty.uniqueCodeFailed'), ok: false });
  }

  async function deleteMembers(memberIds: string[]) {
    if (!supabase || memberIds.length === 0) return;

    const targets = members.filter((m) => memberIds.includes(m.id));
    const walletCount = targets.filter((m) => m.wallet_serial).length;
    const walletNote =
      walletCount > 0
        ? ` ${walletCount} Apple/Google Wallet pass${walletCount === 1 ? '' : 'es'} will be revoked.`
        : '';
    const label =
      memberIds.length === 1 && targets[0]
        ? targets[0].display_name
          ? `${targets[0].display_name} (#${targets[0].member_code})`
          : `#${targets[0].member_code}`
        : `${memberIds.length} members`;

    if (!confirm(`${t('loyalty.remove')} ${label}?${walletNote}`)) return;

    if (memberIds.length === 1) setDeletingMemberId(memberIds[0]!);
    else setBulkDeleting(true);
    setMsg(null);

    try {
      const body = memberIds.length === 1 ? { memberId: memberIds[0] } : { memberIds };
      const { data, error } = await supabase.functions.invoke('loyalty-delete-member', { body });
      if (error) {
        setMsg({
          text: error.message.includes('not found')
            ? 'Delete is not configured on the server yet. Deploy loyalty-delete-member.'
            : error.message,
          ok: false,
        });
        return;
      }

      if (memberIds.length === 1) {
        const result = data as { error?: string; ok?: boolean; walletRevoked?: boolean };
        if (result.error) {
          setMsg({ text: result.error, ok: false });
          return;
        }
        const removed = targets[0];
        if (removed && qrModal?.memberCode === removed.member_code) setQrModal(null);
        setMsg({
          text: result.walletRevoked
            ? 'Member removed and wallet pass revoked.'
            : 'Member removed.',
          ok: true,
        });
      } else {
        const result = data as {
          deleted?: number;
          walletRevoked?: number;
          failed?: { memberId: string; error: string }[];
        };
        const deleted = result.deleted ?? 0;
        const walletRevoked = result.walletRevoked ?? 0;
        const failed = result.failed ?? [];
        if (deleted === 0) {
          setMsg({ text: failed[0]?.error ?? 'Failed to remove members.', ok: false });
          return;
        }
        setMsg({
          text:
            failed.length === 0
              ? `Removed ${deleted} member${deleted === 1 ? '' : 's'}${walletRevoked > 0 ? ` (${walletRevoked} wallet pass${walletRevoked === 1 ? '' : 'es'} revoked)` : ''}.`
              : `Removed ${deleted} of ${memberIds.length}.${walletRevoked > 0 ? ` ${walletRevoked} wallet pass${walletRevoked === 1 ? '' : 'es'} revoked.` : ''} ${failed.length} failed.`,
          ok: failed.length === 0,
        });
        if (qrModal && memberIds.includes(qrModal.memberId)) setQrModal(null);
      }

      setSelectedMemberIds(new Set());
      void load();
    } catch (e) {
      setMsg({ text: e instanceof Error ? e.message : 'Failed to remove member.', ok: false });
    } finally {
      setDeletingMemberId(null);
      setBulkDeleting(false);
    }
  }

  async function deleteMember(member: MemberRow) {
    await deleteMembers([member.id]);
  }

  function toggleMemberSelected(memberId: string) {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }

  function toggleSelectAllFiltered() {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const m of filteredMembers) next.delete(m.id);
      } else {
        for (const m of filteredMembers) next.add(m.id);
      }
      return next;
    });
  }

  if (loading) {
    return (
      <section className="ui-stack">
        <PageHeader title={t('loyalty.title')} />
        <div className="empty-state">{t('common.loading')}</div>
      </section>
    );
  }

  if (!settings?.platform_enabled) {
    return (
      <section className="ui-stack">
        <PageHeader
          title={t('loyalty.title')}
          subtitle={t('loyalty.subtitleDisabled')}
        />
        <div className="panel" style={{ maxWidth: 520 }}>
          <div className="empty-state">
            <p style={{ margin: '0 0 8px', fontWeight: 600 }}>{t('loyalty.notAvailableTitle')}</p>
            <p className="muted" style={{ margin: 0, maxWidth: 360 }}>
              {t('loyalty.notAvailableBody')}
            </p>
          </div>
        </div>
      </section>
    );
  }

  const passesIssued = members.filter((m) => m.wallet_serial).length;

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('loyalty.title')}
        subtitle={t('loyalty.subtitleEnabled', { pointName: POINT })}
      />

      {msg && (
        <div className={`page-feedback ${msg.ok ? 'ok' : 'err'}`} role="status">
          {msg.text}
        </div>
      )}

      <div className="loyalty-grid">
        <div className="panel">
          <div className="panel-header">
            <span className="panel-title">{t('loyalty.settings')}</span>
          </div>

          <div className="form-stack">
            <label className="field form-check-row">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              />
              <span className="field-label">{t('loyalty.enable')}</span>
            </label>

            <div className="form-row-2">
              <label className="field">
                <span className="field-label">{t('loyalty.earnRate')}</span>
                <input
                  type="number"
                  min={0.01}
                  step={1}
                  value={settings.earn_amd_per_coin}
                  onChange={(e) => {
                    const earn = Number(e.target.value) || 50;
                    setSettings({
                      ...settings,
                      earn_amd_per_coin: earn,
                      redeem_amd_per_coin:
                        settings.redeem_amd_per_coin === defaultRedeemAmdPerCoin(earnAmdPerCoin)
                          ? defaultRedeemAmdPerCoin(earn)
                          : settings.redeem_amd_per_coin,
                    });
                  }}
                />
                <p className="field-hint">{t('loyalty.earnHint')}</p>
              </label>

              <label className="field">
                <span className="field-label">{t('loyalty.redeemRate')}</span>
                <input
                  type="number"
                  min={0.01}
                  step={0.1}
                  value={settings.redeem_amd_per_coin}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      redeem_amd_per_coin:
                        Number(e.target.value) || defaultRedeemAmdPerCoin(earnAmdPerCoin),
                    })
                  }
                />
                <p className="field-hint">{t('loyalty.redeemHint')}</p>
              </label>
            </div>

            <button
              type="button"
              className="btn btn-sm btn-ghost"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setRateSummaryOpen((v) => !v)}
            >
              {rateSummaryOpen ? t('loyalty.hideRateSummary') : t('loyalty.rateSummary')}
            </button>

            {rateSummaryOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <RateExample earnAmdPerCoin={earnAmdPerCoin} redeemAmdPerCoin={redeemAmdPerCoin} />
                <div style={{ fontSize: 13 }}>
                  <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
                    POS preview — 10,000 AMD cart, 10% member + 500 {POINT}
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                    <li>Subtotal: {AMD(posPreview.subtotal)}</li>
                    <li>10% tier discount: −{AMD(posPreview.percentDiscountAmd)}</li>
                    <li>
                      {posPreview.coinsRedeemed} {POINT} redeemed: −{AMD(posPreview.coinDiscountAmd)}
                    </li>
                    <li>
                      <strong>Customer pays: {AMD(posPreview.payableTotal)}</strong>
                    </li>
                    <li>
                      {POINT} earned this visit (if tier collects): +{posPreview.coinsEarnedPreview}{' '}
                      {POINT}
                    </li>
                  </ul>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button className="btn" type="button" onClick={() => void save()} disabled={saving}>
                {saving ? t('common.saving') : t('loyalty.saveSettings')}
              </button>
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-header">
            <span className="panel-title">{t('loyalty.walletTitle')}</span>
          </div>

          <div className="form-stack">
            <label className="field">
              <span className="field-label">{t('loyalty.logoText')}</span>
              <input
                value={settings.pass_logo_text}
                onChange={(e) => setSettings({ ...settings, pass_logo_text: e.target.value })}
                placeholder={cafeName || t('loyalty.logoPlaceholder')}
              />
              <p className="field-hint">{t('loyalty.logoHint')}</p>
            </label>

            <p className="field-hint" style={{ margin: 0 }}>
              {t('loyalty.cardTitleAlways', { title: LOYALTY_PASS_CARD_TITLE })}{' '}
              {t('loyalty.balanceShown', { name: LOYALTY_POINT_NAME })}
            </p>

            <label className="field">
              <span className="field-label">{t('loyalty.passColor')}</span>
              <select
                value={settings.pass_color_preset}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    pass_color_preset: e.target.value as SettingsRow['pass_color_preset'],
                  })
                }
              >
                {COLOR_PRESETS.map((c) => (
                  <option key={c} value={c}>
                    {t(`loyalty.color${c[0]!.toUpperCase()}${c.slice(1)}`)}
                  </option>
                ))}
              </select>
            </label>

            <p className="field-hint" style={{ margin: 0 }}>
              {t('loyalty.colorApply')}
            </p>

            <div className="form-actions">
              <button className="btn" type="button" onClick={() => void save()} disabled={saving}>
                {saving ? t('common.saving') : t('loyalty.saveDesign')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {settings.enabled && (
        <div className="panel loyalty-wide-panel loyalty-links-panel">
          <div className="panel-header">
            <div className="loyalty-links-header-left">
              <span className="panel-title">{t('loyalty.linksTitle')}</span>
              <span className="muted" style={{ fontSize: 12 }}>
                {links.length} · {passesIssued} passes
              </span>
            </div>
            {!linkFormOpen && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setLinkFormOpen(true)}
              >
                {t('loyalty.newLink')}
              </button>
            )}
          </div>

          {linkFormOpen && (
            <div className="loyalty-link-form">
              <div className="add-form loyalty-link-add-form">
                <input
                  value={newLinkName}
                  onChange={(e) => setNewLinkName(e.target.value)}
                  placeholder={t('loyalty.namePlaceholder')}
                  onKeyDown={(e) => e.key === 'Enter' && void createLink()}
                  style={{ flex: '1 1 160px', minWidth: 140 }}
                />
                <label className="loyalty-link-check">
                  <input
                    type="checkbox"
                    checked={newLinkCollectCoins}
                    onChange={(e) => setNewLinkCollectCoins(e.target.checked)}
                  />
                  {t('loyalty.collectPoints')}
                </label>
                <label className="loyalty-link-check" title={t('loyalty.customNumberTitle')}>
                  <input
                    type="checkbox"
                    checked={newLinkAllowCustomNumbers}
                    onChange={(e) => setNewLinkAllowCustomNumbers(e.target.checked)}
                  />
                  {t('loyalty.customNumber')}
                </label>
                <button
                  className="btn btn-sm"
                  type="button"
                  disabled={linkBusy || !newLinkName.trim()}
                  onClick={() => void createLink()}
                >
                  {linkBusy ? '…' : t('loyalty.add')}
                </button>
                <button
                  className="btn-ghost btn-sm"
                  type="button"
                  disabled={linkBusy}
                  onClick={closeLinkForm}
                >
                  Cancel
                </button>
              </div>
              <div className="loyalty-category-discounts-wrap">
                <span className="field-label" style={{ fontSize: 12 }}>
                  {t('loyalty.discountByCategory')}
                </span>
                <CategoryDiscountEditor
                  categories={categories}
                  values={newLinkCategoryDiscounts}
                  onChange={setNewLinkCategoryDiscounts}
                />
              </div>
            </div>
          )}

          <table className="table">
            <thead>
              <tr>
                <th>{t('loyalty.colName')}</th>
                <th>{t('loyalty.colDiscount')}</th>
                <th>{t('loyalty.colPoints')}</th>
                <th>{t('loyalty.customNumber')}</th>
                <th>{t('loyalty.colLink')}</th>
                <th>{t('loyalty.colStatus')}</th>
                <th aria-label={t('loyalty.actionsAria')} />
              </tr>
            </thead>
            <tbody>
              {links.map((link) => {
                const url = buildLoyaltyJoinUrl(clientId!, link.slug);
                const isEditing = editLinkId === link.id;
                if (isEditing) {
                  return (
                    <>
                      <tr key={link.id} className="loyalty-link-edit-row">
                        <td>
                          <input
                            className="loyalty-table-input"
                            value={editLinkName}
                            onChange={(e) => setEditLinkName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && void saveEditLink()}
                            style={{ width: '100%', minWidth: 120 }}
                            autoFocus
                            aria-label={t('loyalty.linkNameAria')}
                          />
                        </td>
                        <td colSpan={3} className="muted" style={{ fontSize: 12 }}>
                          {t('loyalty.editDiscounts')}
                        </td>
                        <td className="muted" style={{ fontSize: 12 }}>
                          /{link.slug}
                        </td>
                        <td colSpan={2}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <label className="loyalty-link-check">
                              <input
                                type="checkbox"
                                checked={editLinkCollectCoins}
                                onChange={(e) => setEditLinkCollectCoins(e.target.checked)}
                              />
                              {t('loyalty.collect')}
                            </label>
                            <label className="loyalty-link-check">
                              <input
                                type="checkbox"
                                checked={editLinkAllowCustomNumbers}
                                onChange={(e) => setEditLinkAllowCustomNumbers(e.target.checked)}
                              />
                              {t('loyalty.custom')}
                            </label>
                          </div>
                        </td>
                      </tr>
                      <tr key={`${link.id}-edit-discounts`} className="loyalty-link-edit-row">
                        <td colSpan={7}>
                          <CategoryDiscountEditor
                            categories={categories}
                            values={editLinkCategoryDiscounts}
                            onChange={setEditLinkCategoryDiscounts}
                          />
                          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                            <button
                              className="btn btn-sm"
                              type="button"
                              disabled={linkBusy || !editLinkName.trim()}
                              onClick={() => void saveEditLink()}
                            >
                              {linkBusy ? '…' : 'Save'}
                            </button>
                            <button
                              className="btn-ghost btn-sm"
                              type="button"
                              disabled={linkBusy}
                              onClick={cancelEditLink}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    </>
                  );
                }
                return (
                  <tr key={link.id}>
                    <td>{link.name}</td>
                    <td>
                      {formatCategoryDiscountSummary(
                        parseCategoryDiscounts(link.category_discounts),
                        Number(link.discount_percent) || 0,
                        categories,
                      )}
                    </td>
                    <td>{link.collect_coins ? 'Yes' : 'No'}</td>
                    <td>{link.allow_custom_numbers ? 'Yes' : 'No'}</td>
                    <td>
                      <button
                        className="btn-ghost btn-sm"
                        type="button"
                        title={url}
                        onClick={() => void navigator.clipboard.writeText(url)}
                      >
                        {t('loyalty.copyLink')}
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn-ghost btn-sm"
                        type="button"
                        title={link.active ? 'Click to disable signups' : 'Click to re-enable'}
                        onClick={() => void toggleLinkActive(link)}
                      >
                        {link.active ? 'Active' : 'Off'}
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn-ghost btn-sm"
                        type="button"
                        onClick={() => startEditLink(link)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
              {links.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    {t('loyalty.emptyLinks')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {settings.enabled && (
        <div className="panel loyalty-wide-panel">
          <div className="panel-header">
            <span className="panel-title">
              {t('loyalty.membersTitle', { filtered: filteredMembers.length })}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {selectedFilteredCount > 0 && (
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  disabled={membersBusy}
                  style={{ color: 'var(--danger)' }}
                  onClick={() =>
                    void deleteMembers(filteredMembers.filter((m) => selectedMemberIds.has(m.id)).map((m) => m.id))
                  }
                >
                  {bulkDeleting ? '…' : t('loyalty.removeSelected', { count: selectedFilteredCount })}
                </button>
              )}
              {!memberFormOpen && (
                <button type="button" className="btn btn-sm" onClick={() => setMemberFormOpen(true)}>
                  {t('loyalty.addMember')}
                </button>
              )}
            </div>
          </div>

          <div className="inv-filter-bar loyalty-member-filter-bar">
            <label>
              {t('loyalty.source')}
              <select
                value={memberSourceFilter}
                onChange={(e) => {
                  setMemberSourceFilter(e.target.value);
                  setSelectedMemberIds(new Set());
                }}
              >
                <option value="all">{t('loyalty.allSources')}</option>
                <option value="manual">{t('loyalty.manual')}</option>
                {links.map((link) => (
                  <option key={link.id} value={link.id}>
                    {link.name}
                  </option>
                ))}
                <option value="link_removed">{t('loyalty.linkRemoved')}</option>
              </select>
            </label>
          </div>

          {memberFormOpen && (
            <div className="loyalty-link-form">
              <div className="add-form loyalty-link-add-form">
                <input
                  value={newMemberName}
                  onChange={(e) => setNewMemberName(e.target.value)}
                  placeholder={t('loyalty.namePlaceholder')}
                  aria-label={t('loyalty.memberNameAria')}
                  style={{ flex: '1 1 140px', minWidth: 120 }}
                />
                <input
                  type="tel"
                  value={newMemberPhone}
                  onChange={(e) => setNewMemberPhone(e.target.value)}
                  placeholder={t('loyalty.phoneOptional')}
                  style={{ flex: '1 1 140px', minWidth: 120 }}
                />
                <label className="loyalty-link-check">
                  <input
                    type="checkbox"
                    checked={newMemberCollectCoins}
                    onChange={(e) => setNewMemberCollectCoins(e.target.checked)}
                  />
                  {t('loyalty.collectPoints')}
                </label>
                <button
                  className="btn btn-sm"
                  type="button"
                  disabled={memberBusy || !newMemberName.trim()}
                  onClick={() => void createMember()}
                >
                  {memberBusy ? '…' : t('loyalty.create')}
                </button>
                <button
                  className="btn-ghost btn-sm"
                  type="button"
                  disabled={memberBusy}
                  onClick={closeMemberForm}
                >
                  Cancel
                </button>
              </div>
              <div className="loyalty-category-discounts-wrap">
                <span className="field-label" style={{ fontSize: 12 }}>
                  {t('loyalty.discountByCategory')}
                </span>
                <CategoryDiscountEditor
                  categories={categories}
                  values={newMemberCategoryDiscounts}
                  onChange={setNewMemberCategoryDiscounts}
                />
              </div>
            </div>
          )}

          <p className="panel-body-hint muted" style={{ fontSize: 12 }}>
            {t('loyalty.manualMembersNote')} {t('loyalty.linksTitle')}
            always include Apple / Google Wallet.
          </p>

          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    disabled={membersBusy || filteredMembers.length === 0}
                    aria-label={t('loyalty.selectAllAria')}
                    onChange={toggleSelectAllFiltered}
                  />
                </th>
                <th>{t('loyalty.colCustomerNumber')}</th>
                <th>{t('loyalty.colName')}</th>
                <th>{t('loyalty.source')}</th>
                <th>{t('loyalty.colDiscount')}</th>
                <th>{t('loyalty.colEarnsPoints')}</th>
                <th>{LOYALTY_POINT_NAME}</th>
                <th>{t('loyalty.colWallet')}</th>
                <th>{t('loyalty.colJoined')}</th>
                <th aria-label={t('loyalty.actionsAria')} />
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((m) => (
                <tr key={m.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedMemberIds.has(m.id)}
                      disabled={membersBusy}
                      aria-label={t('loyalty.selectMemberAria', { name: m.display_name ?? m.member_code })}
                      onChange={() => toggleMemberSelected(m.id)}
                    />
                  </td>
                  <td>
                    <code>{m.member_code}</code>
                  </td>
                  <td>{m.display_name ?? '—'}</td>
                  <td>{formatMemberSource(m, linkById, clientId, t)}</td>
                  <td>
                    {formatCategoryDiscountSummary(
                      parseCategoryDiscounts(m.category_discounts),
                      Number(m.discount_percent) || 0,
                      categories,
                    )}
                  </td>
                  <td>{m.collect_coins ? 'Yes' : 'No'}</td>
                  <td>{Number(m.coin_balance).toLocaleString()}</td>
                  <td>{m.wallet_serial ? '✓' : '—'}</td>
                  <td className="muted">{new Date(m.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {!m.wallet_serial && (
                        <button
                          className="btn-ghost btn-sm"
                          type="button"
                          disabled={membersBusy}
                          onClick={() =>
                            setQrModal({
                              memberId: m.id,
                              memberCode: m.member_code,
                              label: m.display_name ?? undefined,
                            })
                          }
                        >
                          {t('loyalty.qr')}
                        </button>
                      )}
                      <button
                        className="btn-ghost btn-sm"
                        type="button"
                        disabled={membersBusy}
                        style={{ color: 'var(--danger)' }}
                        onClick={() => void deleteMember(m)}
                      >
                        {deletingMemberId === m.id ? '…' : t('loyalty.remove')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredMembers.length === 0 && (
                <tr>
                  <td colSpan={9} className="muted">
                    {members.length === 0 ? t('loyalty.emptyMembers') : t('loyalty.emptyFilter')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {qrModal && (
        <div className="modal-overlay" onClick={() => setQrModal(null)}>
          <div className="modal" style={{ maxWidth: 360, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('loyalty.qrTitle')}</h2>
              <button className="btn-ghost btn-sm" type="button" onClick={() => setQrModal(null)}>
                Close
              </button>
            </div>
            {qrModal.label && <p style={{ margin: '0 0 8px' }}>{qrModal.label}</p>}
            <p className="muted" style={{ fontSize: 13, margin: '0 0 16px' }}>
              {t('loyalty.qrBody', { code: qrModal.memberCode })}
            </p>
            <LoyaltyMemberQr
              memberId={qrModal.memberId}
              memberCode={qrModal.memberCode}
              cafeName={cafeName}
              size={220}
            />
          </div>
        </div>
      )}
    </section>
  );
}
